// Les règles des User Fields sont transmises par PMPro sans changer ses réglages.
const requiredMessage = 'Ce champ est nécessaire.';

export function fieldValueError(rule, value) {
  if (Array.isArray(value)) {
    if (rule.required && !value.length) return requiredMessage;
    return value.every((option) => rule.options.includes(String(option))) ? '' : 'Veuillez choisir une option proposée.';
  }
  value = String(value ?? '').trim();
  if (!value || (rule.kind === 'checkbox' && value === '0')) return rule.required ? requiredMessage : '';
  if (rule.maxlength != null && Array.from(value).length > rule.maxlength) return 'Le texte saisi est trop long.';
  switch (rule.kind) {
    case 'phone': return /^[\d\s().-]+$/.test(value) && value.replace(/\D/g, '').length === 10
      ? '' : 'Veuillez entrer un numéro de téléphone de 10 chiffres (ex. : 819 555-1234).';
    case 'person_name': return /^[\p{L}\p{M}\s'’.\-]+$/u.test(value) && /\p{L}/u.test(value)
      ? '' : 'Veuillez entrer un nom valide, sans chiffres. Les accents, apostrophes et tirets sont permis.';
    case 'email': return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Veuillez entrer une adresse courriel valide.';
    case 'url': {
      try {
        const url = new URL(value);
        if (['http:', 'https:'].includes(url.protocol) && url.hostname) return '';
      } catch (_) { /* Afficher le message sous le champ. */ }
      return 'Veuillez entrer une adresse de site web complète (ex. : https://exemple.ca).';
    }
    case 'rbq': return /^(?:\d{10}|\d{4}-\d{4}-\d{2})$/.test(value)
      ? '' : 'Veuillez entrer les 10 chiffres du numéro RBQ (ex. : 1234-5678-90).';
    case 'select': case 'radio': case 'multiselect': case 'select2': case 'checkbox_grouped':
      return rule.options.includes(value) ? '' : 'Veuillez choisir une option proposée.';
    case 'number': return Number.isFinite(Number(value)) && (rule.min == null || Number(value) >= Number(rule.min)) &&
      (rule.max == null || Number(value) <= Number(rule.max)) ? '' : 'Veuillez entrer un nombre dans les limites permises.';
    case 'date': {
      const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00Z`) : null;
      return date && !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value ? '' : 'Veuillez entrer une date valide.';
    }
  }
  return '';
}

function controlsFor(form, name) {
  return Array.from(form.elements).filter((element) => [name, `${name}[]`, `${name}_upload[]`].includes(element.name));
}

function rootFor(control) {
  return control.closest('.pmpro_form_field') || control.parentElement;
}

function activeRule(control, rule) {
  if (control.disabled || !rootFor(control)?.getClientRects().length) return false;
  if (!rule.depends?.length) return true;
  const matches = rule.depends.map((dependency) => controlsFor(control.form, dependency.id).some((element) => {
    if (['radio', 'checkbox'].includes(element.type)) return element.checked && element.value === String(dependency.value);
    if (element.multiple) return Array.from(element.selectedOptions).some((option) => option.value === String(dependency.value));
    return element.value === String(dependency.value);
  }));
  return rule.dependsOr ? matches.some(Boolean) : matches.every(Boolean);
}

function valueFor(control, rule) {
  const controls = controlsFor(control.form, rule.name);
  if (['checkbox_grouped', 'multiselect', 'select2'].includes(rule.kind)) {
    return control.multiple ? Array.from(control.selectedOptions).map((option) => option.value) :
      controls.filter((element) => element.checked).map((element) => element.value);
  }
  if (rule.kind === 'radio') return controls.find((element) => element.checked)?.value || '';
  if (rule.kind === 'checkbox') return control.checked ? control.value || '1' : '';
  return control.value;
}

function errorFor(control, rule) {
  if (!activeRule(control, rule)) return '';
  if (rule.kind === 'file') {
    const controls = controlsFor(control.form, rule.name);
    const files = controls.flatMap((element) => Array.from(element.files || []));
    const gallery = controls.concat(control).map((element) => element.closest?.('[data-gallery-field]')).find(Boolean);
    const deleted = control.form.elements[`pmpro_delete_file_${rule.name}_field`]?.value;
    const hasFile = gallery
      ? !!gallery.querySelector('[data-gallery-existing-item]:not(.is-removed)')
      : rule.hasFile && !deleted;
    if (rule.required && !files.length && !hasFile) return requiredMessage;
    const fileControl = controls.find((element) => element.type === 'file') || control;
    const accepted = (rule.accept || fileControl.accept || '').split(',').map((type) => type.trim().toLowerCase()).filter(Boolean);
    for (const file of files) {
      if (rule.maxBytes > 0 && file.size > rule.maxBytes) return 'Le fichier dépasse la taille maximale permise.';
      if (accepted.length && !accepted.some((type) => type.endsWith('/*') ? file.type.startsWith(type.slice(0, -1)) :
        type.includes('/') ? file.type === type : file.name.toLowerCase().endsWith(`.${type.replace(/^\./, '')}`))) {
        return 'Veuillez choisir un fichier dans un format permis.';
      }
    }
    return '';
  }
  const value = valueFor(control, rule);
  const error = fieldValueError(rule, value);
  if (error) return error;
  if (rule.kind === 'address' && value.trim()) {
    const token = control.form.elements.cc_address_selection?.value;
    let values;
    try { values = JSON.parse(control.dataset.ccAddressValues || 'null'); } catch (_) { values = null; }
    if (!token || !values || Object.entries(values).some(([name, expected]) => {
      const target = control.form.elements[name];
      return target && String(target.value).trim() !== String(expected).trim();
    })) return 'Veuillez sélectionner une adresse dans les suggestions. Toute modification nécessite une nouvelle sélection.';
  }
  if (rule.match && value !== control.form.elements[rule.match]?.value) {
    return rule.kind === 'email' ? 'Les adresses courriel doivent être identiques.' : 'Les mots de passe doivent être identiques.';
  }
  return '';
}

export function validateControl(control) {
  if (!control?.dataset.ccFieldRule || !control.form) return '';
  const rule = JSON.parse(control.dataset.ccFieldRule);
  const message = errorFor(control, rule);
  let error = document.getElementById(control.dataset.ccErrorId);
  if (!error) {
    error = document.createElement('span');
    error.id = control.dataset.ccErrorId;
    error.className = 'cc-field-error';
    error.setAttribute('aria-live', 'polite');
    rootFor(control).appendChild(error);
  }
  error.textContent = message;
  error.hidden = !message;
  controlsFor(control.form, rule.name).forEach((element) => {
    element.setAttribute('aria-invalid', message ? 'true' : 'false');
    element.dataset.ccTouched = '1';
  });
  return message;
}

function initialize(form) {
  if (!['pmpro_form', 'member-profile-edit'].includes(form.id) && !form.querySelector('[data-cc-field-rule]')) return;
  const native = {
    username: 'text', password: 'password', password2: 'password', bemail: 'email', bconfirmemail: 'email',
    user_email: 'email', display_name: 'text', bfirstname: 'person_name', blastname: 'person_name',
    baddress1: 'text', bcity: 'text', bstate: 'text', bzipcode: 'text', bcountry: 'select', bphone: 'phone',
  };
  Array.from(form.elements).forEach((control) => {
    if (!control.name || !native[control.name] || control.dataset.ccFieldRule || control.readOnly || control.type === 'hidden') return;
    const rule = { name: control.name, kind: native[control.name], required: control.required || control.classList.contains('pmpro_form_input-required'),
      options: control.options ? Array.from(control.options).map((option) => option.value) : [] };
    if (control.name === 'password2') rule.match = 'password';
    if (control.name === 'bconfirmemail') rule.match = 'bemail';
    control.dataset.ccFieldRule = JSON.stringify(rule);
    control.dataset.ccErrorId = `${control.id || control.name}_cc_error`;
    control.setAttribute('aria-describedby', `${control.getAttribute('aria-describedby') || ''} ${control.dataset.ccErrorId}`.trim());
    if (rule.kind === 'phone') {
      control.type = 'tel';
      control.inputMode = 'tel';
    }
  });
}

if (typeof document !== 'undefined') {
  const initializeAll = () => Array.from(document.forms).forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll);
  else initializeAll();
  document.addEventListener('focusin', (event) => {
    if (event.target.form) initialize(event.target.form);
  });
  ['blur', 'change'].forEach((type) => document.addEventListener(type, (event) => validateControl(event.target), true));
  document.addEventListener('input', (event) => {
    if (event.isComposing) return;
    if (event.target.dataset?.ccTouched === '1') validateControl(event.target);
    if (event.target.form) event.target.form.querySelectorAll('[data-cc-field-rule][data-cc-touched="1"]').forEach((control) => {
      const rule = JSON.parse(control.dataset.ccFieldRule);
      if (rule.match === event.target.name) validateControl(control);
    });
  });
  document.addEventListener('invalid', (event) => {
    if (event.target.dataset?.ccFieldRule) {
      event.preventDefault();
      validateControl(event.target);
      const first = event.target.form.querySelector('[aria-invalid="true"][data-cc-field-rule]');
      (first || event.target).focus();
    }
  }, true);
  document.addEventListener('submit', (event) => {
    initialize(event.target);
    let first;
    const seen = new Set();
    event.target.querySelectorAll('[data-cc-field-rule]').forEach((control) => {
      const rule = JSON.parse(control.dataset.ccFieldRule);
      if (seen.has(rule.name)) return;
      seen.add(rule.name);
      const candidates = controlsFor(event.target, rule.name).filter((element) => element.dataset.ccFieldRule);
      const preferred = candidates.find((element) => element.type === 'file')
        || candidates.find((element) => element.type !== 'hidden')
        || control;
      if (validateControl(preferred) && !first) first = preferred;
    });
    if (first) {
      event.preventDefault();
      event.stopImmediatePropagation();
      (rootFor(first).querySelector('.ms-options-wrap > button, .select2-selection') || first).focus();
    }
  }, true);
}
